/**
 * Shared dashboard metric card with the colorful gradient/border treatment
 * originally inlined in `client/src/pages/employer/Dashboard.tsx`. Lifted
 * here so broker and employee dashboards can share the same look.
 *
 * Tailwind purge safety: all class strings are kept verbatim in the
 * `colorRecipes` lookup. Do NOT interpolate color into class names — JIT
 * won't pick them up.
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

export type MetricCardColor =
  | "blue"
  | "green"
  | "orange"
  | "purple"
  | "teal"
  | "cyan"
  | "amber"
  | "red";

interface MetricCardProps {
  color: MetricCardColor;
  title: string;
  icon: LucideIcon;
  value: ReactNode;
  subtitle?: ReactNode;
  /** 0–100. Renders a Progress bar above the subtitle when provided. */
  progressValue?: number;
  /** Optional bottom slot for breakdown rows / extra metadata. */
  footer?: ReactNode;
  /** Optional wouter href; wraps the card in a Link with hover state. */
  href?: string;
}

interface Recipe {
  border: string;
  bg: string;
  title: string;
  icon: string;
  value: string;
  subtitle: string;
  progress: string;
}

const colorRecipes: Record<MetricCardColor, Recipe> = {
  blue: {
    border: "border-l-blue-500",
    bg: "bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10",
    title: "text-blue-700 dark:text-blue-300",
    icon: "text-blue-600",
    value: "text-blue-800 dark:text-blue-200",
    subtitle: "text-blue-600 dark:text-blue-400",
    progress: "bg-blue-100 dark:bg-blue-900/20",
  },
  green: {
    border: "border-l-green-500",
    bg: "bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/10",
    title: "text-green-700 dark:text-green-300",
    icon: "text-green-600",
    value: "text-green-800 dark:text-green-200",
    subtitle: "text-green-600 dark:text-green-400",
    progress: "bg-green-100 dark:bg-green-900/20",
  },
  orange: {
    border: "border-l-orange-500",
    bg: "bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-950/20 dark:to-orange-900/10",
    title: "text-orange-700 dark:text-orange-300",
    icon: "text-orange-600",
    value: "text-orange-800 dark:text-orange-200",
    subtitle: "text-orange-600 dark:text-orange-400",
    progress: "bg-orange-100 dark:bg-orange-900/20",
  },
  purple: {
    border: "border-l-purple-500",
    bg: "bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/10",
    title: "text-purple-700 dark:text-purple-300",
    icon: "text-purple-600",
    value: "text-purple-800 dark:text-purple-200",
    subtitle: "text-purple-600 dark:text-purple-400",
    progress: "bg-purple-100 dark:bg-purple-900/20",
  },
  teal: {
    border: "border-l-teal-500",
    bg: "bg-gradient-to-br from-teal-50 to-teal-100/50 dark:from-teal-950/20 dark:to-teal-900/10",
    title: "text-teal-700 dark:text-teal-300",
    icon: "text-teal-600",
    value: "text-teal-800 dark:text-teal-200",
    subtitle: "text-teal-600 dark:text-teal-400",
    progress: "bg-teal-100 dark:bg-teal-900/20",
  },
  cyan: {
    border: "border-l-cyan-500",
    bg: "bg-gradient-to-br from-cyan-50 to-cyan-100/50 dark:from-cyan-950/20 dark:to-cyan-900/10",
    title: "text-cyan-700 dark:text-cyan-300",
    icon: "text-cyan-600",
    value: "text-cyan-800 dark:text-cyan-200",
    subtitle: "text-cyan-600 dark:text-cyan-400",
    progress: "bg-cyan-100 dark:bg-cyan-900/20",
  },
  amber: {
    border: "border-l-amber-500",
    bg: "bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/20 dark:to-amber-900/10",
    title: "text-amber-700 dark:text-amber-300",
    icon: "text-amber-600",
    value: "text-amber-800 dark:text-amber-200",
    subtitle: "text-amber-600 dark:text-amber-400",
    progress: "bg-amber-100 dark:bg-amber-900/20",
  },
  red: {
    border: "border-l-red-500",
    bg: "bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/20 dark:to-red-900/10",
    title: "text-red-700 dark:text-red-300",
    icon: "text-red-600",
    value: "text-red-800 dark:text-red-200",
    subtitle: "text-red-600 dark:text-red-400",
    progress: "bg-red-100 dark:bg-red-900/20",
  },
};

export function MetricCard({
  color,
  title,
  icon: Icon,
  value,
  subtitle,
  progressValue,
  footer,
  href,
}: MetricCardProps) {
  const r = colorRecipes[color];

  const inner = (
    <Card
      className={`border-l-4 ${r.border} ${r.bg} ${href ? "transition-shadow hover:shadow-md cursor-pointer" : ""}`}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className={`text-sm font-medium ${r.title}`}>
          {title}
        </CardTitle>
        <Icon className={`h-5 w-5 ${r.icon}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold ${r.value}`}>{value}</div>
        {typeof progressValue === "number" && (
          <div className="mt-2">
            <Progress value={progressValue} className={`h-2 ${r.progress}`} />
          </div>
        )}
        {subtitle && (
          <p className={`text-xs mt-1 ${r.subtitle}`}>{subtitle}</p>
        )}
        {footer && <div className="mt-2">{footer}</div>}
      </CardContent>
    </Card>
  );

  if (href) {
    return <Link href={href}>{inner}</Link>;
  }
  return inner;
}
